import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import dayjs from 'dayjs'

import { landAxios } from '@Root/utils/landAxios'
import MyhouseMainHouseResponse from '@Types/entities/MyhouseMainHouseResponse'
import { makeHouseLabel, MyhouseLabelType } from '@Root/utils/houseLabelUtils'
import { AUTH_STATUS, FETCH_STATUS, TRADE } from '@Root/contants'
import { formatMoney, MONEY_FORMAT_TYPE } from '@Root/utils/moneyFormatter'
import {
  MYHOUSE_AUTH_FAIL_GUIDE_COMMENT,
  MYHOUSE_AUTH_RESULT_COMMENT,
  OWNER_AUTH_FAIL,
  OWNER_AUTH_PROGRESS,
  OWNER_AUTH_WAIT,
  RESIDENT_AUTH_FAIL,
  RESIDENT_AUTH_PROGRESS,
  RESIDENT_AUTH_WAIT,
  TRADE_EXPIRED,
} from '@Root/contants/myhouseStateGuide'
import { MY_HOUSE_AUTH_METHOD, REAL_ESTATE, REAL_ESTATE_TYPE_OBJ } from '@Root/contants/myhouse'

export const fetchMyhouses = createAsyncThunk('/main/Myhouses', async () => {
  return await (await landAxios.get(`/myhouse/v2`)).data
})

export const fetchHouse = createAsyncThunk('/main/house', async ({ houseNo, complexNo }: { houseNo: number; complexNo: number }) => {
  return await (
    await landAxios.get(`/myhouse/v2/${houseNo}`, {
      params: {
        complexNo: complexNo,
      },
    })
  ).data
})

// { param, files }: { param: MyhouseBoardParamType; files: File[] }

export type MyhouseMainHouseResponseType = {
  houseList: MyhouseMainHouseResponse[] | null
  // routeHouseNo 는 url 에 houseNo 가 있는 상태로 , 이 값을 선택한 집으로 사용하고,
  // routeHouseNo 가 없는경우의 진입에서는 받은 집 리스트의 첫번째 집을 currentHouseNo 로 설정.
  routeHouseNo: number
  tabHouseList: MyhouseLabelType[] | null
  currentHouseNo: number // 0 ~ : 집 번호  , -2 : 응답 받기 전  , -1 : 등록한 집이 없음.
  house: MyhouseMainHouseResponse | null
  houseDetail: HouseDetail | null
  fetchStatus: FETCH_STATUS
  isAllCommercialRealEstate: boolean //등록된 우리집이 '상가(D02)' 또는 '건물(D03)' 타입(상업용 부동산) 뿐인가?
  relocationGrid: boolean
}

export interface MyhouseStateGuide {
  state?: string
  title?: string
  contents?: string
  contentsEmphasis?: string
  routeTitle?: string
  deleteTitle?: string
  modifyTitle?: string
  routeNclick?: string
}

export interface HouseDetail {
  houseNo: number
  tradeType: string
  price: string
  variation: number
  variationStr: string
  assetType: string
  detailAddress: string
  buildName: string
  hoName: string
  isTrendPriceExist: boolean
  homeSize: string
  purchasePrice: string
  provider: string
  contractDate: string
  complexName: string
  phoneList: Array<string>
  complexNo: number | null
  complexInfoUrl: string
  Deposit: string
  complexInfoGuide: string
  ResalePopupInfo: boolean
  ResalePopupInfoDate: string
  someAreaResalePopupInfo: boolean
  tradeExpiredGuide: any
  ownerAuth: boolean
  residentAuth: boolean
  ownerAuthStatus: string
  residentAuthStatus: string
  authApplyDate: string
  ownerAuthCnt: number
  residentAuthCnt: number
  myhouseAuthStateGuide: MyhouseStateGuide | null
  authResultComment: string
}

const initialState: MyhouseMainHouseResponseType = {
  houseList: null,
  currentHouseNo: -2, // 0 ~ : 집 번호  , -2 : 응답 받기 전  , -1 : 등록한 집이 없음.
  house: null,
  tabHouseList: null,
  houseDetail: null,
  fetchStatus: FETCH_STATUS.INITIAL,
  isAllCommercialRealEstate: true,
  relocationGrid: false,
} as MyhouseMainHouseResponseType

function isNotExistsMyhouse(routeHouseNo: number, houseList: MyhouseMainHouseResponse[]) {
  const result = houseList.find((house) => house.houseNo === routeHouseNo)
  return !result
}

export const mainSlice = createSlice({
  name: 'main',
  initialState,
  reducers: {
    updateCurrentHouseNo: (state: MyhouseMainHouseResponseType, action: PayloadAction<{ currentHouseNo: number }>) => {
      state.currentHouseNo = action.payload.currentHouseNo
    },
    updateRouteHouseNo: (state: MyhouseMainHouseResponseType, action: PayloadAction<number>) => {
      state.routeHouseNo = action.payload
    },
    updateRelocationGrid: (state: MyhouseMainHouseResponseType, action: PayloadAction<boolean>) => {
      state.relocationGrid = action.payload
    },
  },
  extraReducers: {
    [fetchMyhouses.fulfilled.type]: (state: MyhouseMainHouseResponseType, action: PayloadAction<MyhouseMainHouseResponse[]>) => {
      const houseList: MyhouseMainHouseResponse[] = action.payload
      state.houseList = houseList
      if (houseList?.length > 0) {
        state.currentHouseNo = !state.routeHouseNo || isNotExistsMyhouse(state.routeHouseNo, houseList) ? action.payload[0].houseNo : state.routeHouseNo

        for (const index in action.payload) {
          if (action.payload[index].realestateTypeCode !== 'D02' && action.payload[index].realestateTypeCode !== 'D03') {
            state.isAllCommercialRealEstate = false
            break
          }
        }
      } else {
        state.currentHouseNo = -1
      }

      state.tabHouseList = makeHouseLabel(houseList)
    },
    [fetchHouse.fulfilled.type]: (state: MyhouseMainHouseResponseType, action: PayloadAction<MyhouseMainHouseResponse>) => {
      state.fetchStatus = FETCH_STATUS.SUCCESS
      const house: MyhouseMainHouseResponse = action.payload
      state.house = house
      state.currentHouseNo = house.houseNo
      state.houseDetail = makeHouseDetail(house)
    },
    [fetchHouse.pending.type]: (state: MyhouseMainHouseResponseType) => {
      state.fetchStatus = FETCH_STATUS.PENDING
    },
    [fetchHouse.rejected.type]: (state: MyhouseMainHouseResponseType) => {
      state.fetchStatus = FETCH_STATUS.FAILURE
    },
  },
})

function makeHouseDetail(house: MyhouseMainHouseResponse): HouseDetail {
  const houseDetail: HouseDetail = {} as HouseDetail

  houseDetail.houseNo = house.houseNo

  // 가격 정보 타이틀 , 가격정보 상세
  // tradeTypeCode 코드는 안내려오는상태 입니다.
  if (house.tradeTypeCode === TRADE.DEAL) {
    if (house.dealAverageAmount) {
      houseDetail.tradeType = '매매 시세'
      houseDetail.provider = house.provider
      houseDetail.price = house.dealAveragePrice

      const priceGap: number = house.dealAverageAmount - house.dealAmount
      houseDetail.variation = priceGap
      if (priceGap !== 0) {
        const priceGapStr = formatMoney(Math.abs(priceGap), MONEY_FORMAT_TYPE.MOBILE)
        houseDetail.variationStr = priceGapStr
      }
      // 매입가
      houseDetail.purchasePrice = house.dealPrice
    } else {
      houseDetail.tradeType = '매입가'
      houseDetail.price = house.dealPrice
    }
    // 기보증금(기전세금)
    houseDetail.Deposit = formatMoney(Math.abs(house.warrantyAmount), MONEY_FORMAT_TYPE.MOBILE)
  } else if (house.tradeTypeCode === TRADE.LEASE) {
    houseDetail.tradeType = '전세 보증금'
    houseDetail.price = house.warrantyPrice
  } else if (house.tradeTypeCode === TRADE.RENT) {
    houseDetail.tradeType = '보증금/월세'
    houseDetail.price = `${house.warrantyPrice}/${house.rentalPrice}`
  }

  // 매물 유형
  if (house.realestateCategoryCode) {
    houseDetail.assetType = REAL_ESTATE_TYPE_OBJ[house.realestateCategoryCode as REAL_ESTATE].LABEL.toString() || house.realestateCategoryName
  } else {
    houseDetail.assetType = house.realestateTypeName
  }

  // 동호수
  if (house.buildName && house.hoName) {
    houseDetail.detailAddress = `${house.buildName}동 ${house.hoName}호`
  } else if (house.buildName) {
    houseDetail.detailAddress = `${house.buildName}동`
  } else if (house.hoName) {
    houseDetail.detailAddress = `${house.hoName}호`
  }
  houseDetail.buildName = house.buildName
  houseDetail.hoName = house.hoName
  houseDetail.isTrendPriceExist = house.isTrendPriceExist

  // 면적
  houseDetail.homeSize = house.pyeongName
  // 입주일자
  houseDetail.contractDate = house.tradeDate && house.tradeDate.replaceAll('-', '.')

  // 전매 제한 문구 노출 여부
  const date = dayjs().format('YYYYMMDD')
  if (Number.parseInt(house.mnpyPsblYmd) > Number.parseInt(date)) {
    if (house.someAreaMnpyRstctYn) {
      houseDetail.someAreaResalePopupInfo = true
      houseDetail.ResalePopupInfo = false
    } else {
      houseDetail.someAreaResalePopupInfo = false
      houseDetail.ResalePopupInfo = true
    }

    houseDetail.ResalePopupInfoDate = dayjs(house.mnpyPsblYmd).format('YYYY.MM.DD.')
  } else {
    houseDetail.someAreaResalePopupInfo = false
    houseDetail.ResalePopupInfo = false
  }

  //전월세 만기일자 만료여부
  if (house.tradeTypeCode !== TRADE.DEAL && house.tradeEndDate) {
    const tradeExpiredDate = dayjs(house.tradeEndDate).endOf('day')
    if (tradeExpiredDate.isBefore(dayjs().startOf('day'))) {
      houseDetail.tradeExpiredGuide = TRADE_EXPIRED
    }
  }

  //관리사무소 번호
  if (house.managementOfficeTelNo) {
    houseDetail.phoneList = [house.managementOfficeTelNo]
    if (house.managementOfficeTelNo2) {
      houseDetail.phoneList.push(house.managementOfficeTelNo2)
    }
    if (house.managementOfficeTelNo3) {
      houseDetail.phoneList.push(house.managementOfficeTelNo3)
    }
  }

  if (houseDetail.phoneList) houseDetail.complexName = house.complexName

  houseDetail.complexInfoUrl = house.complexNo
    ? `${process.env.REACT_APP_SPACE_WEB_URL}/complex/info/${house.complexNo}` // 여기의 /complex/info/ 이 부분 이대로 둬도 되나?
    : ''

  houseDetail.complexNo = house.complexNo

  houseDetail.ownerAuth = house.ownerAuth === 'Y'
  houseDetail.residentAuth = house.residentAuth === 'Y'
  houseDetail.ownerAuthStatus = house.ownerAuthStatus
  houseDetail.residentAuthStatus = house.residentAuthStatus

  houseDetail.ownerAuthCnt = house.ownerAuthCnt
  houseDetail.residentAuthCnt = house.residentAuthCnt

  //매매인 경우는 집주인/입주민 모두 인증 가능(분양권 제외)
  if (house.tradeTypeCode === TRADE.DEAL && house.ownerAuth === 'N' && house.residentAuth === 'N') {
    if (!house.ownerAuthStatus && !house.residentAuthStatus) {
      if (['B01', 'B02', 'B03'].includes(house.realestateTypeCode)) {
        // 입주민 인증 아직 안한상태(분양권은 입주민 인증만 진행)
        houseDetail.myhouseAuthStateGuide = RESIDENT_AUTH_WAIT
      } else {
        // 집주인 인증 아직 안한상태(초기)
        houseDetail.myhouseAuthStateGuide = OWNER_AUTH_WAIT
      }
    } else if (house.ownerAuthStatus === AUTH_STATUS.APPLY1 || house.ownerAuthStatus === AUTH_STATUS.APPLY2) {
      // 집주인 인증 진행중
      houseDetail.myhouseAuthStateGuide = OWNER_AUTH_PROGRESS
    } else if (house.residentAuthStatus === AUTH_STATUS.APPLY1 || house.residentAuthStatus === AUTH_STATUS.APPLY2) {
      // 입주민 인증 진행중
      houseDetail.myhouseAuthStateGuide = RESIDENT_AUTH_PROGRESS
    } else if (
      house.authMethod !== MY_HOUSE_AUTH_METHOD.CERTIFICATE_OF_REALESTATE_REGISTER &&
      (house.residentAuthStatus === AUTH_STATUS.REJECT || house.residentAuthStatus === AUTH_STATUS.FAIL)
    ) {
      // 입주민 인증 실패
      houseDetail.myhouseAuthStateGuide = RESIDENT_AUTH_FAIL
    } else if (
      house.authMethod === MY_HOUSE_AUTH_METHOD.CERTIFICATE_OF_REALESTATE_REGISTER &&
      (house.ownerAuthStatus === AUTH_STATUS.REJECT || house.ownerAuthStatus === AUTH_STATUS.FAIL)
    ) {
      // 집주인 인증 실패
      houseDetail.myhouseAuthStateGuide = OWNER_AUTH_FAIL
    }
  } else if (house.tradeTypeCode !== TRADE.DEAL && house.residentAuth === 'N') {
    //전세,월세인 경우는 입주민 인증만 가능
    if (!house.residentAuthStatus) {
      if (['D02', 'D03'].includes(house.realestateTypeCode)) {
        // 상가, 건물의 경우 집주인 인증 가이드로 노출.
        houseDetail.myhouseAuthStateGuide = OWNER_AUTH_WAIT
      } else {
        // 입주민 인증 아직 안한상태(초기)
        houseDetail.myhouseAuthStateGuide = RESIDENT_AUTH_WAIT
      }
    } else if (house.residentAuthStatus === AUTH_STATUS.APPLY1 || house.residentAuthStatus === AUTH_STATUS.APPLY2) {
      // 입주민 인증 진행중
      houseDetail.myhouseAuthStateGuide = RESIDENT_AUTH_PROGRESS
    } else if (house.residentAuthStatus === AUTH_STATUS.REJECT || house.residentAuthStatus === AUTH_STATUS.FAIL) {
      // 입주민 인증 실패
      houseDetail.myhouseAuthStateGuide = RESIDENT_AUTH_FAIL
    }
  } else if (house.residentAuth === 'Y') {
    //입주민인증 완료했는데, 집주인 인증 진행중인 상태
    if (house.ownerAuthStatus === AUTH_STATUS.APPLY1 || house.ownerAuthStatus === AUTH_STATUS.APPLY2) {
      houseDetail.myhouseAuthStateGuide = OWNER_AUTH_PROGRESS
    } else if (
      house.authMethod === MY_HOUSE_AUTH_METHOD.CERTIFICATE_OF_REALESTATE_REGISTER &&
      (house.ownerAuthStatus === AUTH_STATUS.REJECT || house.ownerAuthStatus === AUTH_STATUS.FAIL)
    ) {
      //입주민인증 완료했는데, 집주인 인증 실패
      houseDetail.myhouseAuthStateGuide = OWNER_AUTH_FAIL
    }
  } else {
    // 집주인이나 입주민 인증을 완료한 경우, 가이드는 노출시키지 않음
    houseDetail.myhouseAuthStateGuide = null
  }
  if (house.authApplyDate) {
    houseDetail.authApplyDate = dayjs(house.authApplyDate).format('YYYY.MM.DD')
  }

  // 집주인/입주민 인증실패 사유
  houseDetail.authResultComment = '실패사유: '
  if (!house.authResultComment) {
    houseDetail.authResultComment = ''
  } else if (house.authResultComment === MYHOUSE_AUTH_RESULT_COMMENT.ADDRESS_AND_NAME_INCORRECT) {
    houseDetail.authResultComment = MYHOUSE_AUTH_FAIL_GUIDE_COMMENT.ADDRESS_NAME_INCORRECT_COMMENT
  } else {
    const splitAuthResultComment = house.authResultComment?.split(',')
    for (const index in splitAuthResultComment) {
      if (splitAuthResultComment[index] === MYHOUSE_AUTH_RESULT_COMMENT.ADDRESS_INCORRECT) {
        houseDetail.authResultComment += MYHOUSE_AUTH_FAIL_GUIDE_COMMENT.ADDRESS_INCORRECT_COMMENT
      } else if (splitAuthResultComment[index] === MYHOUSE_AUTH_RESULT_COMMENT.NAME_INCORRECT) {
        houseDetail.authResultComment += MYHOUSE_AUTH_FAIL_GUIDE_COMMENT.NAME_INCORRECT_COMMENT
      } else if (splitAuthResultComment[index] === MYHOUSE_AUTH_RESULT_COMMENT.FILE_IDENTIFICATION_FAIL) {
        houseDetail.authResultComment += MYHOUSE_AUTH_FAIL_GUIDE_COMMENT.FILE_IDENTIFICATION_FAIL_COMMENT
      } else if (splitAuthResultComment[index] === MYHOUSE_AUTH_RESULT_COMMENT.UNNECESSARY_PERSONAL_INFORMATION) {
        houseDetail.authResultComment += MYHOUSE_AUTH_FAIL_GUIDE_COMMENT.UNNECESSARY_PERSONAL_INFORMATION_COMMENT
      } else {
        houseDetail.authResultComment += splitAuthResultComment[index]
      }
      houseDetail.authResultComment += ' '
    }
  }

  return houseDetail
}

export const { updateCurrentHouseNo, updateRouteHouseNo, updateRelocationGrid } = mainSlice.actions
export default mainSlice.reducer
